/**
 * compact-layout.ts — règles pures d'activation de la mise en page « portrait ».
 *
 * Trois niveaux, du plus large au plus fin :
 *  1. Écran en PORTRAIT (hauteur > largeur), à toute largeur : TPE, téléphone,
 *     tablette en portrait. En paysage (D3, tablette en paysage, PC) : rien ne
 *     change. Le clavier Android réduit la hauteur visible : la décision est
 *     figée tant qu'un champ de saisie a le focus et que la largeur ne change pas
 *     (cf. nextPortraitState).
 *  2. Commerce : flag `caisse_affichage_compact` (défaut ON, coupe-circuit par commerce).
 *  3. Appareil : préférence locale « auto » (défaut) ou « classique ».
 *
 * En plus, sous 600 dp de large (« étroit »), les fenêtres passent en plein écran.
 */

/** Seuil de largeur (px CSS = dp) sous lequel l'écran est « étroit » (fenêtres plein écran). */
export const COMPACT_MAX_WIDTH = 599;
export const COMPACT_MEDIA_QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px)`;

/** Largeur à partir de laquelle la grille produits passe à 3 colonnes. */
export const THREE_COLUMNS_MIN_WIDTH = 480;

/** Clé localStorage de la préférence d'appareil. */
export const COMPACT_DEVICE_PREF_KEY = 'vp_caisse_affichage_compact';

export type CompactDevicePref = 'auto' | 'classique';

export type CompactInputs = {
  isPortrait: boolean;
  tenantEnabled: boolean;
  devicePref: CompactDevicePref;
};

export const resolveCompactLayout = ({
  isPortrait,
  tenantEnabled,
  devicePref,
}: CompactInputs): boolean => isPortrait && tenantEnabled && devicePref === 'auto';

export type ViewportSample = { width: number; height: number; editing: boolean };

/**
 * Orientation après un redimensionnement. Si seule la hauteur a bougé pendant
 * une saisie (clavier virtuel ouvert ou fermé), on garde la décision précédente.
 */
export const nextPortraitState = (
  previous: { portrait: boolean; width: number } | null,
  sample: ViewportSample,
): boolean => {
  if (previous && sample.editing && sample.width === previous.width) return previous.portrait;
  return sample.height > sample.width;
};

/** Part de hauteur (0-1) des produits dans la caisse portrait, bornée. */
export const SPLIT_DEFAULT = 0.55;
export const clampSplit = (ratio: number): number =>
  Number.isFinite(ratio) ? Math.min(0.8, Math.max(0.3, ratio)) : SPLIT_DEFAULT;

export const parseDevicePref = (raw: unknown): CompactDevicePref =>
  raw === 'classique' ? 'classique' : 'auto';

/** Formatage monétaire court « 12,50 € » (affichage seulement). */
export const formatEuro = (amount: number): string => `${amount.toFixed(2).replace('.', ',')} €`;

/** Retour haptique léger (si l'appareil le permet) — silencieux sinon. */
export const hapticTap = (): void => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  } catch {
    // vibration refusée par la WebView : aucun impact
  }
};
